/**
 * FlowShare protocol constants.
 *
 * Everything that both the Worker and the browser must agree on lives here.
 * Keep this file free of runtime dependencies so it can be imported anywhere.
 */

/** Wire protocol version. Bumped only for breaking envelope/payload changes. */
export const PROTOCOL_VERSION = 1;

/** Versions this build can still understand. Older clients are rejected. */
export const SUPPORTED_PROTOCOL_VERSIONS: readonly number[] = [PROTOCOL_VERSION];

/** Human readable protocol identifier used in docs and health output. */
export const PROTOCOL_NAME = 'flowshare-signaling';

/**
 * Room codes are unambiguous when read aloud: no 0/O or 1/I.
 * 32^6 = 1,073,741,824 possible codes.
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 6;
export const ROOM_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;
export const ROOM_CODE_JOIN_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

/** Room sizing and lifetime. */
export const DEFAULT_ROOM_CAPACITY = 8;
export const MIN_ROOM_CAPACITY = 2;
export const MAX_ROOM_CAPACITY = 16;
export const DEFAULT_ROOM_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
export const MAX_ROOM_TTL_MS = 12 * 60 * 60 * 1000;
export const ROOM_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes without traffic
export const ROOM_EXPIRY_WARNING_MS = 5 * 60 * 1000;

/** Connection liveness. */
export const HEARTBEAT_INTERVAL_MS = 15_000;
export const HEARTBEAT_TIMEOUT_MS = 45_000;

/** Limits enforced on signaling traffic. */
export const MAX_SIGNALING_MESSAGE_BYTES = 128 * 1024;
export const MAX_SDP_LENGTH = 96 * 1024;
export const MAX_ICE_CANDIDATE_LENGTH = 8 * 1024;
export const MAX_DEVICE_NAME_LENGTH = 32;
export const MIN_DEVICE_NAME_LENGTH = 1;
export const MAX_REQUEST_ID_LENGTH = 64;
export const MAX_PEER_ID_LENGTH = 32;

/** WebSocket close codes (4000–4999 are application defined). */
export const WS_CLOSE = {
  normal: 1000,
  goingAway: 1001,
  protocolError: 1002,
  policyViolation: 1008,
  messageTooBig: 1009,
  internalError: 1011,
  roomClosed: 4001,
  superseded: 4002,
  unauthorized: 4003,
  roomExpired: 4004,
  removed: 4005,
  rateLimited: 4008,
} as const;

/** Transfer engine. */
export const TRANSFER_CHUNK_SIZE = 64 * 1024;
export const TRANSFER_MIN_CHUNK_SIZE = 16 * 1024;
export const TRANSFER_MAX_CHUNK_SIZE = 256 * 1024;
/** Pause sending when the data channel buffer reaches this size. */
export const TRANSFER_BUFFER_HIGH_WATER = 4 * 1024 * 1024;
/** Resume sending once the buffer drains below this size. */
export const TRANSFER_BUFFER_LOW_WATER = 1024 * 1024;
export const TRANSFER_PROGRESS_INTERVAL_MS = 100;
export const DEFAULT_MAX_CONCURRENT_TRANSFERS = 3;
export const MAX_CONCURRENT_TRANSFERS = 8;
export const MAX_FILES_PER_SELECTION = 500;
export const MAX_FILE_BYTES = 64 * 1024 * 1024 * 1024; // 64 GiB
export const MAX_FILES_PER_TRANSFER_OFFER = 1;
export const MAX_TRANSFER_CONTROL_BYTES = 16 * 1024;
export const TRANSFER_FRAME_VERSION = 1;
export const TRANSFER_FRAME_HEADER_BYTES = 24;
export const TRANSFER_CHUNK_FRAME = 1;

/** Transfer data channel label. One control + one bulk channel per peer. */
export const CONTROL_CHANNEL_LABEL = 'flowshare:control:v1';
export const DATA_CHANNEL_LABEL = 'flowshare:data:v1';

/** STUN servers used when the user has not configured any. */
export const DEFAULT_STUN_SERVERS: readonly string[] = [
  'stun:stun.cloudflare.com:3478',
  'stun:stun.l.google.com:19302',
];

/** Local persistence keys. */
export const STORAGE_KEYS = {
  settings: 'flowshare.settings.v1',
  history: 'flowshare.history.v1',
  session: 'flowshare.session.v1',
  transfers: 'flowshare.transfers.v1',
} as const;

/** API surface. */
export const API_VERSION = 'v1';
export const HEALTH_STATUS = 'ok' as const;

/** Docs / marketing copy that must stay true to the implementation. */
export const PRODUCT_NAME = 'FlowShare';
export const PRODUCT_TAGLINE = 'Share files. Directly. Anywhere.';
